import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseServiceKey = process.env.REACT_APP_SUPABASE_SERVICE_ROLE_KEY;

// Check if required environment variables are available
if (!supabaseUrl || !supabaseServiceKey) {
  console.warn('⚠️ Supabase Auth Admin service: Missing environment variables');
  console.warn('Required: REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_SERVICE_ROLE_KEY');
}

// Create admin client with service role key (only if variables are available)
const supabaseAdmin = supabaseUrl && supabaseServiceKey ? createClient(supabaseUrl, supabaseServiceKey, {
  auth: {
    autoRefreshToken: false,
    persistSession: false
  }
}) : null;

export interface CreateAuthUserInput {
  email: string;
  password?: string;
  email_confirm?: boolean;
  user_metadata?: {
    first_name?: string;
    last_name?: string;
    role?: string;
    global_role?: string;
  };
}

export interface UpdateAuthUserInput {
  email?: string;
  password?: string;
  user_metadata?: {
    first_name?: string;
    last_name?: string;
    role?: string;
    global_role?: string;
  };
}

export const supabaseAuthAdmin = {
  /**
   * Create a new auth user
   */
  async createUser(input: CreateAuthUserInput) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('🔐 Creating Supabase Auth user:', input.email);
      
      const { data, error } = await supabaseAdmin.auth.admin.createUser({
        email: input.email,
        password: input.password || this.generatePassword(),
        email_confirm: input.email_confirm || false,
        user_metadata: input.user_metadata || {}
      });

      if (error) {
        console.error('❌ Error creating auth user:', error);
        throw error;
      }

      console.log('✅ Auth user created successfully:', data.user?.id);
      return data.user;
    } catch (error) {
      console.error('❌ Auth user creation error:', error);
      throw error;
    }
  },

  /**
   * Generate a secure random password
   */
  generatePassword(): string {
    const length = 12;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    
    return password;
  },

  /**
   * Send password reset email to user
   */
  async sendPasswordReset(email: string) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('📧 Sending password reset email to:', email);
      
      // Use the correct method to send password reset email
      const { error } = await supabaseAdmin.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`
      });

      if (error) {
        console.error('❌ Error sending password reset:', error);
        throw error;
      }

      console.log('✅ Password reset email sent successfully');
      return true;
    } catch (error) {
      console.error('❌ Password reset error:', error);
      throw error;
    }
  },

  /**
   * Send welcome email with password reset link
   */
  async sendWelcomeEmail(email: string) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('📧 Sending welcome email to:', email);
      
      // Generate a password reset link for new users
      const { data, error } = await supabaseAdmin.auth.admin.generateLink({
        type: 'recovery',
        email: email,
        options: {
          redirectTo: `${window.location.origin}/reset-password`
        }
      });

      if (error) {
        console.error('❌ Error generating welcome link:', error);
        throw error;
      }

      console.log('✅ Welcome email link generated successfully');
      return data.properties?.action_link;
    } catch (error) {
      console.error('❌ Welcome email error:', error);
      throw error;
    }
  },

  /**
   * Update user metadata
   */
  async updateUserMetadata(userId: string, metadata: any) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('🔄 Updating user metadata for:', userId);
      
      const { data, error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
        user_metadata: metadata
      });

      if (error) {
        console.error('❌ Error updating user metadata:', error);
        throw error;
      }

      console.log('✅ User metadata updated successfully');
      return data.user;
    } catch (error) {
      console.error('❌ User metadata update error:', error);
      throw error;
    }
  },

  /**
   * Update user password
   */
  async updateUserPassword(userId: string, password: string) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('🔑 Updating user password for:', userId);
      
      const { data, error } = await supabaseAdmin.auth.admin.updateUserById(userId, {
        password: password
      });

      if (error) {
        console.error('❌ Error updating user password:', error);
        throw error;
      }

      console.log('✅ User password updated successfully');
      return data.user;
    } catch (error) {
      console.error('❌ User password update error:', error);
      throw error;
    }
  },

  /**
   * Delete auth user
   */
  async deleteUser(userId: string) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('🗑️ Deleting auth user:', userId);
      
      const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);

      if (error) {
        console.error('❌ Error deleting auth user:', error);
        throw error;
      }

      console.log('✅ Auth user deleted successfully');
      return true;
    } catch (error) {
      console.error('❌ Auth user deletion error:', error);
      throw error;
    }
  },

  /**
   * Get user by ID
   */
  async getUserById(userId: string) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('🔍 Getting auth user by ID:', userId);
      
      const { data, error } = await supabaseAdmin.auth.admin.getUserById(userId);

      if (error) {
        console.error('❌ Error getting auth user:', error);
        throw error;
      }

      console.log('✅ Auth user retrieved successfully');
      return data.user;
    } catch (error) {
      console.error('❌ Auth user retrieval error:', error);
      throw error;
    }
  },

  /**
   * List all users (with pagination)
   */
  async listUsers(page: number = 1, perPage: number = 50) {
    if (!supabaseAdmin) {
      throw new Error('Supabase Auth Admin service is not configured. Please check your environment variables.');
    }
    
    try {
      console.log('📋 Listing auth users, page:', page);
      
      const { data, error } = await supabaseAdmin.auth.admin.listUsers({
        page: page,
        perPage: perPage
      });

      if (error) {
        console.error('❌ Error listing auth users:', error);
        throw error;
      }

      console.log('✅ Auth users listed successfully:', data.users?.length || 0);
      return data;
    } catch (error) {
      console.error('❌ Auth users listing error:', error);
      throw error;
    }
  }
};
